import { createOptionsFromEntities } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/helpers/options.helper';
import { HaftpflichtSchaden, HaftpflichtSchadenWithAufstellung, SelectOption } from '@gsm/entities';
import { AND_OPERATOR, DYNAMIC_FORM_CONTROL_TYPE_GROUP, DYNAMIC_FORM_CONTROL_TYPE_INPUT, DYNAMIC_FORM_CONTROL_TYPE_SELECT, DynamicFormOptionConfig, MATCH_VISIBLE } from '@ng-dynamic-forms/core';
import { compareProps } from '../../helpers/compare-props.helper';
import { getDisplayName } from '../../helpers/display-name.helper';
import { MATCH_SELECT_VISIBLE } from '../../matchers/select-visible.matcher';
import { TYPE_CHIP_CHOICE_MULTIPLE } from '../chip-choice-multiple/chip-choice-multiple.model';
import { TYPE_INFO } from '../info/info.model';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { HaftpflichtSchadenVorhanden } from '../shared-models/haftpflichtschaden.model';
import { HaftpflichtGeschaedigtePersonAdditional } from './haftpflicht-geschaedigte-person.model';

const VERHAELTNIS_OPTIONS: DynamicFormOptionConfig<string>[] = [
  {
    label: 'Gesellschaftsverhältnis',
    value: 'GESELLSCHAFT'
  },
  {
    label: 'Lohn- / Dienstverhältnis',
    value: 'LOHN'
  },
  {
    label: 'Verwandtschaftsverhältnis',
    value: 'VERWANDTSCHAFT'
  }
];

const VERWANDTSCHAFTS_OPTIONS: SelectOption[] = [
  {
    label: 'Eltern',
    value: 'ELTERN'
  },
  {
    label: 'Enkelkinder',
    value: 'ENKELKINDER'
  },
  {
    label: 'Geschwister',
    value: 'GESCHWISTER'
  },
  {
    label: 'Großeltern',
    value: 'GROSSELTERN'
  },
  {
    label: 'Kinder',
    value: 'KINDER'
  },
  {
    label: 'Anderes Verwandtschaftsverhältnis',
    value: 'ANDERES'
  }
];

const WOHNVERHAELTNIS_OPTIONS: SelectOption[] = [
  {
    label: 'Selber Haushalt, gleiche wirtschafliche Einheit',
    value: 'SELBER_HAUSHALT_GLEICHE_EINHEIT'
  },
  {
    label: 'Selber Haushalt, getrennte wirtschafliche Einheit',
    value: 'SELBER_HAUSHALT_GENTRENNTE_EINHEIT'
  },
  {
    label: 'Nicht selber Haushalt',
    value: 'NICHT_SELBER_HAUSHALT'
  }
];

const KONTAKT_MAIL_OPTION: DynamicFormOptionConfig<string> = {
  label: 'Benachrichtigung per Brief',
  value: 'MAIL'
};
const KONTAKT_EMAIL_OPTION: DynamicFormOptionConfig<string> = {
  label: 'Benachrichtigung per E-Mail',
  value: 'EMAIL'
};

const ADRESSE_FIELDS = [
  'plz',
  'ort',
  'strasse',
  'hausNummer'
];
export const KONTAKT_FIELDS = [
  ...ADRESSE_FIELDS,
  'email'
];

export function HAFTPFLICHT_GESCHAEDIGTE_PERSON_TEMPLATE(
  value: HaftpflichtSchaden,
  {
    geschPersList,
    paperProcessOverride = false,
    updateClaim = false,
    detailsNachbringen = false
  }: HaftpflichtGeschaedigtePersonAdditional = {}
): AnyFormControlModel[] {
  const {
    id,
    vorhanden,
    geschaedigtePerson
  } = value || {};

  const {
    vorname,
    nachname,
    adresse: {
               plz,
               ort,
               strasse,
               hausNummer
             } = {} as any,
    email,
    telephoneNumber,
    kontaktTyp,
    verhaeltnis,
    verhaeltnisList,
    verwandschaftsVerhaeltnisTyp,
    anderesVerwandschaftsVerhaeltnis,
    wohnVerhaeltnis
  } = geschaedigtePerson || {};

  const kontaktOptions = getKontaktOptionsFromValue(value);

  return [
    {
      id:         'id',
      type:       DYNAMIC_FORM_CONTROL_TYPE_INPUT,
      hidden:     true,
      additional: {
        forceLog: true
      },
      value:      id
    },
    {
      id:            'geschaedigtePerson_info',
      type:          TYPE_INFO,
      content:       'Wer ist - aus Sicht des Versicherungsnehmers - die geschädigte Person?',
      isInstruction: true
    },
    {
      id:            'geschaedigtePerson_hint',
      type:          TYPE_INFO,
      content:
                     'Die Erfassung der Forderung ist nach Angabe der Personendaten möglich. Bei nachgebrachten Personendaten ist noch keine Erfassung der Forderung möglich. Diese können dann im Nachbringenprozess zur Person angegeben werden.',
      isInstruction: false
    },
    {
      id:            'geschaedigtePerson',
      type:          DYNAMIC_FORM_CONTROL_TYPE_SELECT,
      label:         'Geschädigte Person',
      required:      true,
      compareWithFn: (a, b) => compareProps([
        'correlationId',
        'kundeId'
      ], a, b, true),
      // disable if updateClaim and detailsNachbringen, if person is vorhanden
      disabled:      vorhanden === HaftpflichtSchadenVorhanden.Vorhanden && updateClaim && detailsNachbringen,
      options:       [
        ...(paperProcessOverride ? [
          {
            label: 'Siehe Beilage',
            value: HaftpflichtSchadenVorhanden.SieheBeilage
          }
        ] : []),
        ...(geschPersList || [])
          .filter(person => person)
          .map(person => ({
            label: getDisplayName([
              person.vorname,
              person.nachname
            ]),
            value: person
          })),
        {
          label: 'Die Personendaten der geschädigten Person erfassen.',
          value: HaftpflichtSchadenVorhanden.Erfassen
        },
        {
          label: 'Die Personendaten der geschädigten Person werden nachgebracht.',
          value: HaftpflichtSchadenVorhanden.Nachgebracht
        }
      ],
      errorMessages: {
        required: '{{ label }} ist ein Pflichtfeld.'
      },
      value:
                     !vorhanden && paperProcessOverride
                       ? HaftpflichtSchadenVorhanden.SieheBeilage
                       : vorhanden !== HaftpflichtSchadenVorhanden.Vorhanden
                         ? vorhanden
                         : geschPersList.find(geschPerson =>
                           geschPerson.kundeId
                             ? geschPerson.kundeId === geschaedigtePerson.kundeId
                             : geschPerson.correlationId === geschaedigtePerson.correlationId
                         )
    },
    {
      id:        'geschaedigtePersonNameErfassen',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      hidden:    true,
      relations: [
        {
          match: MATCH_VISIBLE,
          when:  [
            {
              id:    'geschaedigtePerson',
              value: HaftpflichtSchadenVorhanden.SieheBeilage
            },
            {
              id:    'geschaedigtePerson',
              value: HaftpflichtSchadenVorhanden.Erfassen
            }
          ]
        }
      ],
      group:     [
        {
          id:            'vorname',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          placeholder:   'Bitte geben Sie den Vornamen des Geschädigten ein.',
          label:         'Vorname',
          validators:    {
            maxLength: 25
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie einen gültigen Vornamen ein',
            maxLength: 'Bitte geben Sie maximal 25 Zeichen ein.'
          },
          value:         vorname
        },
        {
          id:            'nachname',
          placeholder:   'Bitte geben Sie den Nachnamen des Geschädigten oder die Firma ein.',
          label:         'Nachname/Firmenname',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          required:      true,
          validators:    {
            minLength: 2,
            required:  null,
            maxLength: 60
          },
          errorMessages: {
            minLength: 'Bitte geben Sie einen gültigen Nachnamen ein.',
            required:  '{{ label }} ist ein Pflichtfeld.',
            maxLength: 'Bitte geben Sie maximal 60 Zeichen ein.'
          },
          value:         nachname
        }
      ]
    },
    {
      id:        'geschaedigtePersonErfassen',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      hidden:    true,
      relations: [
        {
          match: MATCH_VISIBLE,
          when:  [
            {
              id:    'geschaedigtePerson',
              value: HaftpflichtSchadenVorhanden.Erfassen
            }
          ]
        }
      ],
      group:     [
        {
          id:            'plz',
          placeholder:   'Bitte geben Sie eine Postleitzahl an.',
          label:         'PLZ',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          validators:    {
            maxLength: 10,
            alphanum:  {
              letterRequired: false
            }
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie eine gültige {{label}} ein.',
            maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
          },
          value:         plz
        },
        {
          id:            'ort',
          placeholder:   'Bitte geben Sie einen Ort an.',
          label:         'Ort',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          validators:    {
            minLength: 2,
            maxLength: 30
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie einen gültigen {{label}} ein.',
            minLength: 'Bitte geben Sie mindestens 2 Zeichen ein.',
            required:  '{{ label }} ist ein Pflichtfeld.',
            maxLength: 'Bitte geben Sie maximal 30 Zeichen ein.'
          },
          value:         ort
        },
        {
          id:            'strasse',
          placeholder:   'Bitte geben Sie eine Straße an.',
          label:         'Straße',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          validators:    {
            minLength: 2,
            alphanum:  null,
            maxLength: 45
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie eine gültige {{label}} ein.',
            minLength: 'Bitte geben Sie mindestens 2 Zeichen ein.',
            required:  '{{ label }} ist ein Pflichtfeld.',
            maxLength: 'Bitte geben Sie maximal 45 Zeichen ein.'
          },
          value:         strasse
        },
        {
          id:            'hausNummer',
          placeholder:   'Bitte geben Sie eine Hausnummer an.',
          label:         'Hausnummer',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          validators:    {
            alphanum:  {
              letterRequired: false
            },
            maxLength: 7
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie eine gültige {{label}} ein.',
            maxLength: 'Bitte geben Sie maximal 7 Zeichen ein.'
          },
          value:         hausNummer
        },
        {
          id:            'email',
          placeholder:   'Bitte geben Sie eine E-Mail an.',
          label:         'Email',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          validators:    {
            email:     null,
            maxLength: 300
          },
          errorMessages: {
            email:     'Bitte geben Sie eine gültige {{label}} ein.',
            maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
          },
          value:         email
        },
        {
          id:            'telephoneNumber',
          placeholder:   'Bitte geben Sie eine Telefonnummer an.',
          label:         'Telefonnummer',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          validators:    {
            phone:     null,
            maxLength: 300
          },
          errorMessages: {
            phone:     'Bitte geben Sie eine gültige {{label}} ein.',
            required:  '{{ label }} ist ein Pflichtfeld.',
            maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
          },
          value:         telephoneNumber
        }
      ]
    },
    {
      id:   'kontaktTyp_group',
      type: DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      // Hidden is also set in the component, see method _configureKontaktOptions().
      hidden: !kontaktOptions.length,
      group:  [
        {
          id:            'kontaktTyp_instruction',
          type:          TYPE_INFO,
          content:       'Wie soll der Geschädigte über den Schaden informiert werden?',
          isInstruction: true
        },
        {
          id:       'kontaktTyp',
          type:     DYNAMIC_FORM_CONTROL_TYPE_SELECT,
          label:    'Benachrichtigung',
          required: true,
          options:  kontaktOptions,
          value:    kontaktTyp
        }
      ]
    },
    {
      id:        'verhaeltnis_group',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      label:     'Verhältnis',
      hidden:    true,
      relations: [
        {
          match: MATCH_VISIBLE,
          when:  [
            {
              id:    'geschaedigtePerson',
              value: HaftpflichtSchadenVorhanden.Erfassen
            }
          ]
        }
      ],
      group:     [
        {
          id:            'verhaeltnis_instruction',
          type:          TYPE_INFO,
          content:
                         'Stehen der Versicherungsnehmer und die geschädigte Person in einem Lohn-, Gesellschafter- (Gesellschafter bei einer Firma) oder Verwandtschaftsverhältnis zueinander?',
          isInstruction: true
        },
        {
          id:       'verhaeltnis',
          type:     DYNAMIC_FORM_CONTROL_TYPE_SELECT,
          label:    'Verhältnis vorhanden?',
          required: true,
          options:  [
            {
              label: 'Ja',
              value: true
            },
            {
              label: 'Nein',
              value: false
            }
          ],
          value:    verhaeltnis
        },
        {
          id:            'verhaeltnisList_instruction',
          type:          TYPE_INFO,
          content:       'In welchem Verhältnis stehen der Versicherungsnehmer und die geschädigte Person zueinander?',
          isInstruction: true,
          hidden:        true,
          relations:     [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'verhaeltnis',
                  value: true
                }
              ]
            }
          ]
        },
        {
          id:        'verhaeltnisList',
          type:      TYPE_CHIP_CHOICE_MULTIPLE,
          label:     'Verhältnis',
          required:  true,
          hidden:    true,
          options:   createOptionsFromEntities(VERHAELTNIS_OPTIONS, 'label'),
          relations: [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'verhaeltnis',
                  value: true
                }
              ]
            }
          ],
          value:     verhaeltnisList
        },
        {
          id:        'verwandtschaftsverhaeltnis_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:     'Verwandtschaftsverhältnis',
          hidden:    true,
          relations: [
            {
              match:    MATCH_SELECT_VISIBLE,
              operator: AND_OPERATOR,
              when:     [
                {
                  id:    'verhaeltnisList',
                  value: {
                    label: 'Verwandtschaftsverhältnis',
                    value: 'VERWANDTSCHAFT'
                  }
                },
                {
                  id:    'verhaeltnis',
                  value: true
                }
              ]
            }
          ],
          group:     [
            {
              id:            'verwandschaftsVerhaeltnisTyp',
              type:          DYNAMIC_FORM_CONTROL_TYPE_SELECT,
              label:         'Verwandtschaftsverhältnis',
              required:      true,
              optionMatcher: ['value'],
              options:       createOptionsFromEntities(VERWANDTSCHAFTS_OPTIONS, 'label'),
              value:         verwandschaftsVerhaeltnisTyp
            },
            {
              id:            'anderesVerwandschaftsVerhaeltnis',
              type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
              label:         'Anderes Verwandschaftsverhältnis',
              required:      true,
              hidden:        true,
              minLength:     2,
              validators:    {
                maxLength: 300
              },
              errorMessages: {
                maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
              },
              relations:     [
                {
                  match: MATCH_SELECT_VISIBLE,
                  when:  [
                    {
                      id:    'verwandschaftsVerhaeltnisTyp',
                      value: {
                        label: 'Anderes Verwandtschaftsverhältnis',
                        value: 'ANDERES'
                      }
                    }
                  ]
                }
              ],
              value:         anderesVerwandschaftsVerhaeltnis
            }
          ]
        },
        {
          id:        'wohnverhaeltnis_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:     'Wohnverhältnis',
          hidden:    true,
          relations: [
            {
              match: MATCH_SELECT_VISIBLE,
              when:  [
                {
                  id:    'verhaeltnis',
                  value: false
                },
                {
                  id:    'verhaeltnisList',
                  value: {
                    label: 'Verwandtschaftsverhältnis',
                    value: 'VERWANDTSCHAFT'
                  }
                }
              ]
            }
          ],
          group:     [
            {
              id:            'wohnVerhaeltnis',
              type:          DYNAMIC_FORM_CONTROL_TYPE_SELECT,
              label:         'Wohnverhältnis',
              required:      true,
              optionMatcher: ['value'],
              options:       createOptionsFromEntities(WOHNVERHAELTNIS_OPTIONS, 'label'),
              value:         wohnVerhaeltnis
            }
          ]
        }
      ]
    }
  ];
}

export function getKontaktOptionsFromValue(value: HaftpflichtSchadenWithAufstellung): DynamicFormOptionConfig<string>[] {
  if (value.vorhanden !== HaftpflichtSchadenVorhanden.Erfassen) {
    return [];
  }

  const hasEmail = !!value?.geschaedigtePerson?.email;
  const adresse = value?.geschaedigtePerson?.adresse;
  const hasAdresseInfo = !!adresse && ADRESSE_FIELDS.some(prop => !!adresse[prop]);

  const options = [];
  if (hasAdresseInfo) {
    options.push(KONTAKT_MAIL_OPTION);
  }
  if (hasEmail) {
    options.push(KONTAKT_EMAIL_OPTION);
  }
  return options;
}
